import { MouseEvent, useCallback, useMemo, useState } from 'react';
import cn from 'classnames';

import { convertQuillDeltaToHtml } from 'functions/convertQuillDeltaToHtml';
import { IReactQuillDelta } from 'APIPackage/KnmModule/interfaces/IReactQuillDelta';
import { ViolationStatus } from 'KnmErknmPackage/ErknmViolationsModule/components/ViolationStatus';
import { IKnmErknmFormViolation } from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { IconCollapse } from 'UIPackage/IconsModule/components/IconCollapse';
import { IKnmErknmFormRequirementNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormRequirementNpa';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { Button } from 'UIPackage/MainModule/components/Button';
import { ViolationStatusesEnum } from 'APIPackage/KnmModule/enums/ViolationStatusesEnum';
import { InspectionResultActionTypeEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultActionTypeEnum';
import { InspectionResultFieldEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import { ErknmInspectionResultMessage } from 'KnmErknmPackage/MainModule/components/ErknmInspectionResultMessageMessage';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { NonClosableTooltip } from 'UIPackage/MainModule/components/NonClosableTooltip';
import { IconQuestion } from 'UIPackage/IconsModule/components/IconQuestion';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { ReactQuillEditorField } from 'UIPackage/MainModule/components/ReactQuillEditorField';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { IBooleanSelectOption } from 'KnmPackage/MainModule/interfaces/IBooleanSelectOption';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { ErknmDecisionViolationFixItems } from 'DecisionPackage/DecisionViolationModule/components/ErknmDecisionViolationFixItems';

import styles from './ViolationItem.module.scss';

interface IViolationItemProps {
  violation: IKnmErknmFormViolation;
  requirementNpa?: IKnmErknmFormRequirementNpa;
  violationName: string;
  checkboxDisabled: boolean;
  formRef: IDecisionFormRef | undefined;
  id: string;
  currentErpId?: string;
  marginBottom?: boolean;
  inBody?: boolean;
  hasUnsignedAct?: boolean;
}

const negativeSelectOption = booleanSelectOptions.find(option => !option.value);

export const ViolationItem = ({
  violation,
  requirementNpa,
  violationName,
  checkboxDisabled,
  formRef,
  id,
  currentErpId,
  marginBottom,
  hasUnsignedAct = false,
  inBody,
}: IViolationItemProps) => {
  const [isOpen, setIsOpen] = useState(false);
  // @ts-ignore
  const reasons = formRef?.current?.getFieldState(`reasons`)?.value;
  const erpId = reasons?.[0]?.links?.[0]?.erpId;

  /**
   * Функция изменения состояния компонента Открыт/Закрыт
   */
  const onChangeOpen = useCallback((e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(val => !val);
  }, []);

  /**
   * Обработчик нажатия кнопки "Оценить представленные материалы"
   */
  const onClickOpenPrescription = useCallback(() => {
    if (violation.prescriptionLink) {
      const url = new URL(violation.prescriptionLink, window.location.origin);

      url.searchParams.set('expandAllInPrescription', 'true');
      window.open(url.toString(), '_blank');
    }
  }, [violation]);

  const onChangeIsFixedOnPlace = useCallback(
    (v: IBooleanSelectOption) => {
      if (!v.value && violation.repeatExplanation) {
        // @ts-ignore
        formRef?.current?.change(`${violationName}.repeatExplanation` as keyof IKnmForm);
      }
      if (v.value === true) {
        // @ts-ignore
        formRef?.current.change(`${fieldName}.includeInSubject`, false);
      }
    },
    [formRef, violation.explanation, violationName],
  );

  const handleViolationResolvedChange = useCallback(
    (v: IBooleanSelectOption) => {
      if (v.value === false) {
        // @ts-ignore
        formRef?.current.change(`${violationName}.fixes`);
      }
      if (v.value === true) {
        // @ts-ignore
        formRef?.current.change(`${fieldName}.includeInSubject`, false);
      }
    },
    [formRef, violationName],
  );

  /**
   * Вычитывание ошибки при проверках за 5 денй
   */
  const inspectionResultError = useMemo(() => {
    const inspectionRed = requirementNpa?.inspectionResults?.find(
      i =>
        i.resultActionType === InspectionResultActionTypeEnum.LIGHT_RED &&
        (i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_REQUIREMENTS_NPA ||
          i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_STRUCTURAL_UNIT),
    );
    const inspectionYellow = requirementNpa?.inspectionResults?.find(
      i =>
        i.resultActionType === InspectionResultActionTypeEnum.LIGHT_YELLOW &&
        (i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_REQUIREMENTS_NPA ||
          i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_STRUCTURAL_UNIT),
    );
    let inspectionMessage = '';

    if (requirementNpa?.inspectionResults) {
      inspectionMessage = requirementNpa.inspectionResults
        ?.filter(
          i =>
            i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_REQUIREMENTS_NPA ||
            i.inspectedField === InspectionResultFieldEnum.ROT_REGISTRY_STRUCTURAL_UNIT,
        )
        .map(i => i.inspectionMessage)
        .join(', ');
    }

    return {
      inspectionRed,
      inspectionYellow,
      inspectionMessage,
    };
  }, [requirementNpa?.inspectionResults]);

  return (
    <div
      key={violation.id}
      className={cn(styles.ViolationItemWrapper, {
        [styles.Transparent]: !violation.includeInSubject,
        [styles.RequirementsNpaItemBlockInputInspectionResultYellow]: !!inspectionResultError.inspectionYellow,
        [styles.RequirementsNpaItemBlockInputInspectionResultRed]: !!inspectionResultError.inspectionRed,
        [styles.MarginBottom]: marginBottom,
      })}
    >
      <div className={cn(styles.Row, styles.Header)}>
        {(inspectionResultError.inspectionRed || inspectionResultError.inspectionYellow) && (
          <ErknmInspectionResultMessage
            inspectionResult={{
              inspectedField: InspectionResultFieldEnum.ROT_REGISTRY_REQUIREMENTS_NPA,
              inspectionMessage: inspectionResultError.inspectionMessage,
              resultActionType: inspectionResultError.inspectionRed
                ? InspectionResultActionTypeEnum.LIGHT_RED
                : InspectionResultActionTypeEnum.LIGHT_YELLOW,
            }}
          />
        )}
        <div className={styles.InnerHeaderWrapper}>
          <div className={cn(styles.Cell, styles.CellWidthDescription)}>
            <div className={styles.DescriptionWrapper}>
              <span>Нарушение №{violation.number}</span>
              <div
                /* eslint-disable-next-line react/no-danger */
                dangerouslySetInnerHTML={{
                  __html: convertQuillDeltaToHtml(violation.descriptionReactQuillEditor as IReactQuillDelta),
                }}
              />
              <div className={styles.IncludeInSubjectCheckboxWrapper}>
                <CheckboxField
                  id={`includeInSubject-${violation.id}`}
                  name={`${violationName}.includeInSubject`}
                  label="Включить в предмет проверки"
                  theme="system"
                  type="checkbox"
                  disabled={checkboxDisabled || violation.status === ViolationStatusesEnum.FIXED}
                />
              </div>
            </div>
          </div>
          <div className={cn(styles.Cell, styles.CellWidthStatus)}>
            <div className={styles.StatusWrapper}>
              <div className={styles.StatusInnerWrapper}>
                <ViolationStatus status={violation.status} />
              </div>
              {violation.status === ViolationStatusesEnum.APPROVING && (
                <Button
                  color="primary"
                  text={'Оценить представленные материалы'}
                  type={'button'}
                  onClick={onClickOpenPrescription}
                />
              )}
              {violation.resolvedIn && violation.resolvedIn !== currentErpId && (
                <span className={styles.StatusNotificationYellow}>Нарушение устранено в другом мероприятии</span>
              )}
            </div>
            <div className={cn(styles.Arrow, { [styles.ArrowRotate]: isOpen })} onClick={onChangeOpen}>
              <IconCollapse className={styles.OpenButtonIcon} />
            </div>
          </div>
        </div>
      </div>
      {isOpen && (
        <div className={styles.InnerWrapper}>
          <Row>
            <FieldBlock
              className={styles.FieldBlock}
              label={
                <Row align="center" margin="none" notSpace>
                  <span>
                    {getTitleFieldWithRedStar({
                      label: 'Нарушение устранено',
                      field: `${violationName}.repeatCorrected`,
                      isDecision: true,
                    })}
                  </span>
                  <NonClosableTooltip
                    content={
                      <div>
                        Предметом текущего мероприятия было нарушение, включенное в предписание по мероприятию №{' '}
                        <a href={violation.prescriptionLink} target="_blank" rel="noreferrer">
                          {erpId}
                        </a>
                        . Необходимо установить значение «Да» в поле, если необходимо подтвердить факт устранения
                        нарушения
                      </div>
                    }
                  >
                    <IconQuestion className={styles.TooltipIcon} />
                  </NonClosableTooltip>
                </Row>
              }
              placementLabel="left"
              color={'black'}
              labelSize="col-3"
              labelFontSize="small"
              type="not-space"
              id={`${id}.repeatCorrected`}
            >
              <SelectInputField
                name={`${violationName}.repeatCorrected`}
                options={booleanSelectOptions}
                isClearable={false}
                defaultValue={negativeSelectOption}
                disabled={inBody || violation.status === ViolationStatusesEnum.FIXED}
                onChange={handleViolationResolvedChange}
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock
              label="Нарушение устранено в ходе мероприятия"
              placementLabel="left"
              color={'black'}
              className={styles.FieldBlock}
              labelSize="col-3"
              labelFontSize="small"
              type="not-space"
              id={`${id}.repeatCorrectedInEvent`}
            >
              <SelectInputField
                name={`${violationName}.repeatCorrectedInEvent`}
                options={booleanSelectOptions}
                isClearable={false}
                defaultValue={negativeSelectOption}
                onChange={onChangeIsFixedOnPlace}
                disabled={inBody || violation.status === ViolationStatusesEnum.FIXED}
              />
            </FieldBlock>
          </Row>
          {(violation.repeatCorrected?.value === false || violation.repeatCorrectedInEvent?.value) && (
            <Row>
              <FieldBlock
                label="Пояснение"
                placementLabel="left"
                labelSize="col-3"
                labelFontSize="small"
                type="not-space"
                className={cn(styles.FieldBlock, styles.GroupMarginBottom)}
                color="black"
              >
                <ReactQuillEditorField
                  name={`${violationName}.repeatExplanation`}
                  placeholder="Введите пояснение (при необходимости)"
                  handleCustomChange={(e: string) => {
                    // @ts-ignore
                    formRef?.current?.change(`${violationName}.repeatExplanation`, e);
                  }}
                  disabled={inBody || violation.status === ViolationStatusesEnum.FIXED}
                />
              </FieldBlock>
            </Row>
          )}
          {violation.repeatCorrected?.value && (
            <ErknmDecisionViolationFixItems
              violation={violation}
              formRef={formRef}
              name={violationName}
              isFixedFieldName="repeatCorrected"
              watchOnly={violation.isNew}
              isPaperless={true}
              inAct
              isDisabledByDirty={false}
              isKnm={false}
              isPm={false}
              isDecision={true}
              id={id}
              hasUnsignedAct={hasUnsignedAct}
              isFixed={violation.repeatCorrected?.value}
              isRepeatCheck
            />
          )}
        </div>
      )}
    </div>
  );
};
